import type { TabId } from "../ui/tabCatalog.js";

/**
 * 웹(CrazyGames) 배너 노출 surface 판정 — 순수 함수 (Phaser/SDK 의존 0, 단위테스트 1급).
 *
 * CrazyGames 배너는 진행·전투 화면에서 노출하지 않는다(정책):
 * - 허용: shop, codex, settings(모달 열린 동안)
 * - 금지: battle, dungeon, moduleMerge, moduleMastery, rebirth, statUpgrade 및 기타 진행형 탭
 *
 * 모바일 LevelPlay 배너는 이 함수를 사용하지 않는다(항상 상단 표시, pause 없음).
 */

/** CrazyGames 배너가 허용되는 탭 목록. */
const BANNER_ALLOWED_TABS: ReadonlySet<TabId> = new Set<TabId>(["shop", "codex"]);

export interface BannerSurfaceInput {
  /** 현재 활성 탭. */
  readonly activeTab: TabId;
  /** 설정 모달이 열려 있는지. */
  readonly settingsOpen: boolean;
  /** 광고 제거 보유 여부. */
  readonly adFree: boolean;
  /** featureFlags에서 CrazyGames 배너 활성 여부. */
  readonly enabled: boolean;
}

export interface BannerSurfaceResult {
  /** 배너를 표시(show)할지 제거(remove)할지. */
  readonly action: "show" | "remove";
  /** 웹 전용: 배너 노출 중 게임 진행 일시정지 여부. */
  readonly pauseGameplay: boolean;
}

/**
 * 현재 상태로 CrazyGames 배너 표시 여부 + pause 필요성을 결정한다.
 * adFree이거나 비활성이면 항상 remove. 설정 모달 열림이면 탭 무관 show.
 * 허용 탭(shop, codex)에서만 show + pauseGameplay.
 */
export function resolveBannerSurface(input: BannerSurfaceInput): BannerSurfaceResult {
  if (input.adFree || !input.enabled) {
    return { action: "remove", pauseGameplay: false };
  }
  const allowed = input.settingsOpen || BANNER_ALLOWED_TABS.has(input.activeTab);
  return {
    action: allowed ? "show" : "remove",
    pauseGameplay: allowed,
  };
}
